import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Sparkles,
  Search,
  TrendingUp,
  XCircle,
  Gauge,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  maskCNPJ,
  maskPhone,
  onlyDigits,
  parseCurrencyInput,
  formatBRL,
  parseNumberInput,
  formatNumber,
} from "@/lib/masks";

const SCALE_OPTIONS = [
  { value: 0, label: "Inexistente", hint: "Não existe controle, processo ou evidência." },
  { value: 1, label: "Informal", hint: "Existe de forma verbal, isolada ou sem padrão." },
  { value: 2, label: "Parcial", hint: "Existe parcialmente, com controle manual ou incompleto." },
  { value: 3, label: "Estruturado", hint: "Existe processo formal, responsável definido e rotina." },
  { value: 4, label: "Avançado", hint: "Controle formal, indicador, sistema e melhoria contínua." },
];

const EMPTY = {
  company: "",
  cnpj: "",
  fleet_manager: "",
  phone: "",
  email: "",
  consultant: "",
  audit_date: "",
  vehicles_qty: "",
  drivers_qty: "",
  fleet_type: "",
  operation_type: "",
  city_uf: "",
  notes: "",
};

const Diagnostic = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [options, setOptions] = useState({ consultant: [], fleet_type: [], operation_type: [] });
  const [step, setStep] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [answers, setAnswers] = useState({}); // qid -> {score} or {value}
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [preview, setPreview] = useState(null); // pre-diag preview result
  const [previewLoading, setPreviewLoading] = useState(false);
  const [terminated, setTerminated] = useState(false);
  const [showTutorialScreen, setShowTutorialScreen] = useState(false);
  const [tutorialSeen, setTutorialSeen] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/survey/public"), api.get("/options")])
      .then(([r1, r2]) => {
        setSections(r1.data.sections || []);
        setQuestions(r1.data.questions || []);
        const grouped = { consultant: [], fleet_type: [], operation_type: [] };
        (r2.data || []).forEach((o) => {
          if (grouped[o.type]) grouped[o.type].push(o);
        });
        setOptions(grouped);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  // Sort sections: pre-diagnosis first (preserving order), then normal (preserving order).
  const orderedSections = useMemo(() => {
    const pre = sections.filter((s) => s.is_pre_diagnosis).sort((a, b) => a.order - b.order);
    const normal = sections.filter((s) => !s.is_pre_diagnosis).sort((a, b) => a.order - b.order);
    return [...pre, ...normal];
  }, [sections]);

  const preDiagCount = useMemo(
    () => orderedSections.filter((s) => s.is_pre_diagnosis).length,
    [orderedSections]
  );
  const hasPreDiag = preDiagCount > 0;

  // Flow stages:
  // 0: cadastro
  // 1..preDiagCount: pre-diag sections
  // preDiagCount+1: pre-diag result intermediary (only if hasPreDiag)
  // next: normal sections
  // last: review
  const intermediateStep = hasPreDiag ? preDiagCount + 1 : -1;
  const totalSteps =
    1 + orderedSections.length + (hasPreDiag ? 1 : 0) + 1; // cadastro + sections + (intermediate?) + review

  const getSectionForStep = (s) => {
    if (s === 0) return null;
    let idx = s - 1; // remove cadastro offset
    if (hasPreDiag && s > preDiagCount) {
      if (s === intermediateStep) return null; // intermediate page
      idx = s - 2; // skip intermediate page index
    }
    return orderedSections[idx] || null;
  };

  const progress = useMemo(
    () => Math.round((step / (totalSteps - 1)) * 100),
    [step, totalSteps]
  );

  const sectionQuestions = (sectionId) =>
    questions.filter((q) => q.section_id === sectionId);

  const fetchCNPJ = async () => {
    const digits = onlyDigits(data.cnpj);
    if (digits.length !== 14) {
      toast.error("Informe um CNPJ com 14 dígitos.");
      return;
    }
    setCnpjLoading(true);
    try {
      const { data: r } = await api.get(`/cnpj/${digits}`);
      setData((d) => ({
        ...d,
        company: r.company || d.company,
        email: r.email || d.email,
        phone: r.phone ? maskPhone(r.phone) : d.phone,
        city_uf: r.city_uf || d.city_uf,
      }));
      toast.success(`Dados encontrados: ${r.company}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCnpjLoading(false);
    }
  };

  const validateCadastro = () => {
    if (!data.company.trim()) {
      toast.error("Informe ao menos o nome da empresa.");
      return false;
    }
    return true;
  };

  const isSectionComplete = (sectionId, isFinance) => {
    const qs = sectionQuestions(sectionId);
    if (qs.length === 0) return true;
    return qs.every((q) => {
      const a = answers[q.id];
      if (!a) return false;
      const type = q.question_type || "scale";
      if (type === "currency" || type === "number") {
        return a.value !== undefined && a.value !== null;
      }
      if (type === "single_choice") {
        return typeof a.text === "string" && a.text.trim() !== "";
      }
      if (type === "text") {
        return typeof a.text === "string" && a.text.trim() !== "";
      }
      // scale (default)
      return a.score !== undefined && a.score !== null;
    });
  };

  const buildAnswersArr = () =>
    Object.entries(answers).map(([question_id, a]) => ({
      question_id,
      score: a.score ?? null,
      value: a.value ?? null,
      text: a.text ?? null,
    }));

  const fetchPreDiagPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data: r } = await api.post("/diagnostics/preview-prediag", {
        ...data,
        vehicles_qty: Number(data.vehicles_qty) || 0,
        drivers_qty: Number(data.drivers_qty) || 0,
        email: data.email || null,
        answers: buildAnswersArr(),
      });
      setPreview(r);
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    } finally {
      setPreviewLoading(false);
    }
  };

  const goNext = async () => {
    if (step === 0 && !validateCadastro()) return;
    // Show a brief guided tutorial once, right before the first Q&A section.
    if (step === 0 && !tutorialSeen) {
      setShowTutorialScreen(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const currentSection = getSectionForStep(step);
    if (currentSection) {
      if (!isSectionComplete(currentSection.id)) {
        toast.error("Responda todas as perguntas desta sessão antes de avançar.");
        return;
      }
      toast.success(`Sessão "${currentSection.name}" concluída!`, { duration: 1500 });
    }
    // If next step is the intermediate, fetch preview before showing it
    const nextStep = step + 1;
    if (hasPreDiag && nextStep === intermediateStep) {
      try {
        await fetchPreDiagPreview();
      } catch (_) {
        return;
      }
    }
    setStep((s) => Math.min(s + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrev = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startQuestions = () => {
    setTutorialSeen(true);
    setShowTutorialScreen(false);
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const continueAfterPreview = () => {
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const terminateAfterPreview = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        vehicles_qty: Number(data.vehicles_qty) || 0,
        drivers_qty: Number(data.drivers_qty) || 0,
        email: data.email || null,
        answers: buildAnswersArr(),
        status: "parcial_pre_diagnostico",
      };
      await api.post("/diagnostics", payload);
      setTerminated(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        vehicles_qty: Number(data.vehicles_qty) || 0,
        drivers_qty: Number(data.drivers_qty) || 0,
        email: data.email || null,
        answers: buildAnswersArr(),
        status: "completo",
      };
      const { data: res } = await api.post("/diagnostics", payload);
      navigate(`/diagnostico/enviado/${res.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B8DB3]">
        <Loader2 className="animate-spin mr-2" /> Carregando diagnóstico...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050608] text-white">
      <header className="border-b border-[#1C1F26] sticky top-0 z-20 bg-[#050608]/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 hover:opacity-80 transition"
            data-testid="back-home-btn"
          >
            <Logo withText={false} size={36} />
            <div className="hidden sm:block">
              <div className="text-xs text-[#6B8DB3] uppercase tracking-[0.28em]">
                Diagnóstico
              </div>
              <div className="text-sm font-semibold">Maturidade em Gestão de Frota</div>
            </div>
          </button>
          <div className="text-xs text-[#6B8DB3] font-mono">
            Etapa <span className="text-white">{step + 1}</span> / {totalSteps}
          </div>
        </div>
        <div className="h-1.5 bg-[#1C1F26] w-full">
          <div
            className="h-full transition-all duration-500 ease-out"
            style={{
              width: `${progress}%`,
              background:
                "linear-gradient(90deg, #013A63 0%, #6B8DB3 50%, #B9262B 100%)",
            }}
            data-testid="progress-bar"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 pb-32">
        {terminated ? (
          <TerminatedStep onRestart={() => navigate("/")} />
        ) : showTutorialScreen ? (
          <TutorialStep onStart={startQuestions} />
        ) : step === 0 ? (
          <CadastroStep
            data={data}
            setData={setData}
            options={options}
            cnpjLoading={cnpjLoading}
            fetchCNPJ={fetchCNPJ}
          />
        ) : hasPreDiag && step === intermediateStep ? (
          <PreDiagnosticResultStep
            preview={preview}
            loading={previewLoading}
            onContinue={continueAfterPreview}
            onTerminate={terminateAfterPreview}
            submitting={submitting}
            hasMoreSections={orderedSections.length > preDiagCount}
          />
        ) : step === totalSteps - 1 ? (
          <ReviewStep
            data={data}
            sections={orderedSections}
            questions={questions}
            answers={answers}
          />
        ) : (
          (() => {
            const sec = getSectionForStep(step);
            if (!sec) return null;
            return (
              <SectionStep
                section={sec}
                questions={sectionQuestions(sec.id)}
                answers={answers}
                setAnswer={(qid, payload) =>
                  setAnswers({ ...answers, [qid]: { ...(answers[qid] || {}), ...payload } })
                }
                currentIndex={step}
                total={orderedSections.length}
              />
            );
          })()
        )}
      </main>

      {!terminated && !showTutorialScreen && !(hasPreDiag && step === intermediateStep) && (
      <nav className="fixed bottom-0 inset-x-0 border-t border-[#1C1F26] bg-[#050608]/95 backdrop-blur z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-3">
          <Button
            variant="outline"
            disabled={step === 0}
            onClick={goPrev}
            data-testid="prev-step-btn"
            className="border-[#3A3F47] text-white hover:bg-[#1C1F26] disabled:opacity-40"
          >
            <ArrowLeft size={16} className="mr-2" /> Voltar
          </Button>

          <div className="text-xs text-[#6B8DB3] hidden sm:block">
            {progress}% concluído
          </div>

          {step < totalSteps - 1 ? (
            <Button
              onClick={goNext}
              data-testid="next-step-btn"
              className="bg-[#B9262B] hover:bg-[#9a1e22] text-white font-semibold px-7"
            >
              Avançar <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-diagnostic-btn"
              className="bg-[#22B673] hover:bg-[#1a9b60] text-white font-semibold px-7 glow-success"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" /> Enviando...
                </>
              ) : (
                <>
                  Finalizar e ver resultado <Sparkles size={16} className="ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </nav>
      )}
    </div>
  );
};

const SectionHeader = ({ eyebrow, title, subtitle }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mb-3">
      {eyebrow}
    </div>
    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
    {subtitle && <p className="mt-2 text-[#9aa3ad] text-base">{subtitle}</p>}
  </div>
);

const FieldLabel = ({ children, required }) => (
  <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider mb-2 block">
    {children} {required && <span className="text-[#B9262B]">*</span>}
  </Label>
);

const TutorialStep = ({ onStart }) => {
  const [demoValue, setDemoValue] = useState(3);
  const demoOption = SCALE_OPTIONS.find((o) => o.value === demoValue);

  return (
    <div className="fade-slide" data-testid="tutorial-step">
      <SectionHeader
        eyebrow="Antes de começar"
        title="Como funciona a dinâmica das perguntas"
        subtitle="Cada pergunta avalia o grau de maturidade de uma prática da sua gestão de frota. Veja um exemplo rápido antes de começar."
      />

      <div className="mt-8 p-5 rounded-xl border border-[#6B8DB3]/40 bg-[#0A2342]/10 flex items-start gap-3">
        <Gauge size={18} className="text-[#6B8DB3] mt-0.5 shrink-0" />
        <p className="text-sm text-[#9aa3ad] leading-relaxed">
          Para cada afirmação, escolha a nota de <span className="text-white font-semibold">0 a 4</span> que
          melhor descreve a situação atual da sua empresa — de <span className="text-white">"não existe"</span>{" "}
          até <span className="text-white">"estruturado e em melhoria contínua"</span>. Não existe resposta
          certa ou errada: responda com base na realidade de hoje.
        </p>
      </div>

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mb-3">
          Exemplo guiado — toque em uma opção para ver como funciona
        </div>
        <div
          className="p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]/70"
          data-testid="tutorial-example-card"
        >
          <div className="flex items-start gap-3 mb-5">
            <div className="font-mono text-xs text-[#B9262B] mt-1">EX</div>
            <div className="flex-1">
              <div className="text-white font-medium text-base sm:text-lg leading-snug">
                Existe um responsável formalmente definido pela gestão da frota?
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {SCALE_OPTIONS.map((opt) => {
              const selected = demoValue === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDemoValue(opt.value)}
                  data-testid={`tutorial-demo-answer-${opt.value}`}
                  title={opt.hint}
                  className={[
                    "group relative flex flex-col items-center justify-center text-center p-3 rounded-lg border transition-all min-h-[68px]",
                    selected
                      ? "border-[#B9262B] bg-[#B9262B]/15"
                      : "border-[#3A3F47] bg-[#0e1015] hover:border-[#6B8DB3]/50 hover:bg-[#1C1F26]",
                  ].join(" ")}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span
                      className={`text-base font-mono font-bold ${
                        selected ? "text-[#B9262B]" : "text-[#6B8DB3]"
                      }`}
                    >
                      {opt.value}
                    </span>
                    {selected && <CheckCircle2 size={14} className="text-[#B9262B]" />}
                  </div>
                  <div
                    className={`text-xs font-semibold ${selected ? "text-white" : "text-[#E6E6E8]"}`}
                  >
                    {opt.label}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-4 text-xs text-[#9aa3ad] leading-relaxed" data-testid="tutorial-demo-hint">
            <span className="text-[#22B673] font-semibold">
              Nota {demoValue} — {demoOption?.label}:
            </span>{" "}
            {demoOption?.hint}
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={onStart}
          data-testid="tutorial-start-btn"
          className="bg-[#B9262B] hover:bg-[#9a1e22] text-white font-semibold px-7"
        >
          Entendi, vamos começar <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

const CadastroStep = ({ data, setData, options, cnpjLoading, fetchCNPJ }) => {
  return (
    <div className="fade-slide">
      <SectionHeader
        eyebrow="Etapa 1"
        title="Dados Cadastrais"
        subtitle="Informe o CNPJ para preenchimento automático ou complete manualmente."
      />

      {/* CNPJ field with lookup */}
      <div className="mt-8">
        <FieldLabel>CNPJ</FieldLabel>
        <div className="flex gap-2">
          <Input
            data-testid="field-cnpj"
            value={data.cnpj}
            placeholder="00.000.000/0000-00"
            onChange={(e) => setData({ ...data, cnpj: maskCNPJ(e.target.value) })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                fetchCNPJ();
              }
            }}
            className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
          />
          <Button
            type="button"
            onClick={fetchCNPJ}
            disabled={cnpjLoading}
            data-testid="cnpj-lookup-btn"
            className="bg-[#013A63] hover:bg-[#02507f] text-white h-11 px-5 whitespace-nowrap"
          >
            {cnpjLoading ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <>
                <Search size={14} className="mr-2" /> Buscar
              </>
            )}
          </Button>
        </div>
        <div className="text-[11px] text-[#6B8DB3] mt-1.5">
          Consulta automática na Receita Federal via BrasilAPI.
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-5">
        <div className="sm:col-span-2">
          <FieldLabel required>Empresa</FieldLabel>
          <Input
            data-testid="field-company"
            value={data.company}
            onChange={(e) => setData({ ...data, company: e.target.value })}
            className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
          />
        </div>

        <div>
          <FieldLabel>Responsável pela frota</FieldLabel>
          <Input
            data-testid="field-fleet_manager"
            value={data.fleet_manager}
            onChange={(e) => setData({ ...data, fleet_manager: e.target.value })}
            className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
          />
        </div>

        <div>
          <FieldLabel>Telefone</FieldLabel>
          <Input
            data-testid="field-phone"
            value={data.phone}
            placeholder="(00) 00000-0000"
            onChange={(e) => setData({ ...data, phone: maskPhone(e.target.value) })}
            className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
          />
        </div>

        <div>
          <FieldLabel>E-mail</FieldLabel>
          <Input
            data-testid="field-email"
            type="email"
            value={data.email}
            onChange={(e) => setData({ ...data, email: e.target.value })}
            className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
          />
        </div>

        <div>
          <FieldLabel>Consultor(a) UniGR</FieldLabel>
          <Select
            value={data.consultant}
            onValueChange={(v) => setData({ ...data, consultant: v })}
          >
            <SelectTrigger
              data-testid="field-consultant"
              className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
            >
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1F26] border-[#3A3F47] text-white">
              {options.consultant.length === 0 ? (
                <div className="px-3 py-2 text-xs text-[#6B8DB3]">
                  Nenhum consultor cadastrado.
                </div>
              ) : (
                options.consultant.map((o) => (
                  <SelectItem key={o.id} value={o.value}>
                    {o.value}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div>
          <FieldLabel>Data da auditoria</FieldLabel>
          <Input
            type="date"
            data-testid="field-audit_date"
            value={data.audit_date}
            onChange={(e) => setData({ ...data, audit_date: e.target.value })}
            className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
          />
        </div>

        <div>
          <FieldLabel>Quantidade de veículos</FieldLabel>
          <Input
            type="number"
            min={0}
            data-testid="field-vehicles_qty"
            value={data.vehicles_qty}
            onChange={(e) => setData({ ...data, vehicles_qty: e.target.value })}
            className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
          />
        </div>

        <div>
          <FieldLabel>Quantidade de motoristas</FieldLabel>
          <Input
            type="number"
            min={0}
            data-testid="field-drivers_qty"
            value={data.drivers_qty}
            onChange={(e) => setData({ ...data, drivers_qty: e.target.value })}
            className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
          />
        </div>

        <div>
          <FieldLabel>Tipo de frota</FieldLabel>
          <Select
            value={data.fleet_type}
            onValueChange={(v) => setData({ ...data, fleet_type: v })}
          >
            <SelectTrigger
              data-testid="field-fleet_type"
              className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
            >
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1F26] border-[#3A3F47] text-white">
              {options.fleet_type.map((o) => (
                <SelectItem key={o.id} value={o.value}>
                  {o.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <FieldLabel>Tipo de operação</FieldLabel>
          <Select
            value={data.operation_type}
            onValueChange={(v) => setData({ ...data, operation_type: v })}
          >
            <SelectTrigger
              data-testid="field-operation_type"
              className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
            >
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1F26] border-[#3A3F47] text-white">
              {options.operation_type.map((o) => (
                <SelectItem key={o.id} value={o.value}>
                  {o.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="sm:col-span-2">
          <FieldLabel>Cidade / UF</FieldLabel>
          <Input
            data-testid="field-city_uf"
            value={data.city_uf}
            placeholder="São Paulo / SP"
            onChange={(e) => setData({ ...data, city_uf: e.target.value })}
            className="bg-[#1C1F26] border-[#3A3F47] text-white h-11"
          />
        </div>

        <div className="sm:col-span-2">
          <FieldLabel>Observações gerais</FieldLabel>
          <Textarea
            data-testid="field-notes"
            rows={4}
            value={data.notes}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
            className="bg-[#1C1F26] border-[#3A3F47] text-white"
          />
        </div>
      </div>
    </div>
  );
};

const SectionStep = ({ section, questions, answers, setAnswer, currentIndex, total }) => {
  const isFinance = section.name === "Finanças";
  const completed = questions.filter((q) => {
    const a = answers[q.id];
    if (!a) return false;
    return isFinance ? a.value != null : a.score != null;
  }).length;
  const pct = questions.length ? Math.round((completed / questions.length) * 100) : 100;

  return (
    <div className="fade-slide">
      <SectionHeader
        eyebrow={`Sessão ${currentIndex} de ${total}`}
        title={section.name}
        subtitle={section.description}
      />

      <div className="mt-6 flex items-center gap-4">
        <div className="flex-1 h-1.5 bg-[#1C1F26] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#22B673] transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-xs font-mono text-[#6B8DB3]">
          {completed}/{questions.length}
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="mt-12 p-8 rounded-xl border border-dashed border-[#3A3F47] text-center text-[#6B8DB3]">
          Esta sessão ainda não possui perguntas cadastradas.
        </div>
      ) : (
        <>
          {!isFinance && (
            <div className="mt-8 p-4 rounded-xl border border-[#3A3F47] bg-[#0e1015]">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mb-3">
                Legenda da escala de respostas
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {SCALE_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    className="text-xs leading-snug p-2 rounded border border-[#1C1F26] bg-[#1C1F26]"
                  >
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-mono font-bold text-[#B9262B]">{opt.value}</span>
                      <span className="font-semibold text-white">{opt.label}</span>
                    </div>
                    <div className="text-[11px] text-[#9aa3ad]">{opt.hint}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-8 space-y-6">
            {questions.map((q, i) => {
              const type = q.question_type || "scale";
              if (type === "currency" || type === "number") {
                return (
                  <FinanceQuestionCard
                    key={q.id}
                    question={q}
                    index={i + 1}
                    value={answers[q.id]?.value ?? null}
                    onChange={(v) => setAnswer(q.id, { value: v, score: null, text: null })}
                  />
                );
              }
              if (type === "single_choice") {
                return (
                  <ChoiceQuestionCard
                    key={q.id}
                    question={q}
                    index={i + 1}
                    value={answers[q.id]?.text ?? null}
                    onChange={(v) => setAnswer(q.id, { text: v, score: null, value: null })}
                  />
                );
              }
              if (type === "text") {
                return (
                  <TextQuestionCard
                    key={q.id}
                    question={q}
                    index={i + 1}
                    value={answers[q.id]?.text ?? ""}
                    onChange={(v) => setAnswer(q.id, { text: v, score: null, value: null })}
                  />
                );
              }
              return (
                <QuestionCard
                  key={q.id}
                  question={q}
                  index={i + 1}
                  value={answers[q.id]?.score ?? null}
                  onChange={(v) => setAnswer(q.id, { score: v, value: null, text: null })}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const QuestionCard = ({ question, index, value, onChange }) => {
  return (
    <div
      className="p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]/70 transition-all hover:border-[#6B8DB3]/40"
      data-testid={`question-card-${question.id}`}
    >
      <div className="flex items-start gap-3 mb-5">
        <div className="font-mono text-xs text-[#B9262B] mt-1">
          {String(index).padStart(2, "0")}
        </div>
        <div className="flex-1">
          <div className="text-white font-medium text-base sm:text-lg leading-snug">
            {question.text}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {SCALE_OPTIONS.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              data-testid={`answer-${question.id}-${opt.value}`}
              title={opt.hint}
              className={[
                "group relative flex flex-col items-center justify-center text-center p-3 rounded-lg border transition-all min-h-[68px]",
                selected
                  ? "border-[#B9262B] bg-[#B9262B]/15"
                  : "border-[#3A3F47] bg-[#0e1015] hover:border-[#6B8DB3]/50 hover:bg-[#1C1F26]",
              ].join(" ")}
            >
              <div className="flex items-center gap-2 mb-0.5">
                <span
                  className={`text-base font-mono font-bold ${
                    selected ? "text-[#B9262B]" : "text-[#6B8DB3]"
                  }`}
                >
                  {opt.value}
                </span>
                {selected && <CheckCircle2 size={14} className="text-[#B9262B]" />}
              </div>
              <div
                className={`text-xs font-semibold ${
                  selected ? "text-white" : "text-[#E6E6E8]"
                }`}
              >
                {opt.label}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const FinanceQuestionCard = ({ question, index, value, onChange }) => {
  const isCurrency = question.question_type === "currency";
  const display = value !== null && value !== undefined
    ? (isCurrency ? formatBRL(value) : formatNumber(value))
    : "";

  return (
    <div
      className="p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]/70 transition-all hover:border-[#6B8DB3]/40"
      data-testid={`finance-card-${question.id}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="font-mono text-xs text-[#B9262B] mt-1">
          {String(index).padStart(2, "0")}
        </div>
        <div className="flex-1">
          <div className="text-white font-medium text-base sm:text-lg leading-snug">
            {question.text}
          </div>
          {question.risk && (
            <div className="text-[11px] text-[#6B8DB3] mt-1">{question.risk}</div>
          )}
        </div>
      </div>

      <Input
        data-testid={`finance-input-${question.id}`}
        inputMode="numeric"
        value={display}
        placeholder={isCurrency ? "R$ 0,00" : "0"}
        onChange={(e) => {
          const parsed = isCurrency
            ? parseCurrencyInput(e.target.value)
            : parseNumberInput(e.target.value);
          onChange(parsed.value);
        }}
        className="bg-[#0e1015] border-[#3A3F47] text-white h-12 text-lg font-semibold tracking-wide"
      />
    </div>
  );
};

const ChoiceQuestionCard = ({ question, index, value, onChange }) => {
  const options = Array.isArray(question.options) ? question.options : [];
  return (
    <div
      className="p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]/70 transition-all hover:border-[#6B8DB3]/40"
      data-testid={`choice-card-${question.id}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="font-mono text-xs text-[#B9262B] mt-1">
          {String(index).padStart(2, "0")}
        </div>
        <div className="flex-1">
          <div className="text-white font-medium text-base sm:text-lg leading-snug">
            {question.text}
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {options.length === 0 ? (
          <div className="text-xs text-[#6B8DB3]">Nenhuma opção configurada.</div>
        ) : (
          options.map((opt, i) => {
            const selected = value === opt;
            return (
              <button
                key={i}
                type="button"
                onClick={() => onChange(opt)}
                data-testid={`choice-${question.id}-${i}`}
                className={[
                  "w-full text-left flex items-center gap-3 p-3 rounded-lg border transition-all",
                  selected
                    ? "border-[#B9262B] bg-[#B9262B]/10"
                    : "border-[#3A3F47] bg-[#0e1015] hover:border-[#6B8DB3]/50",
                ].join(" ")}
              >
                <div
                  className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                    selected ? "border-[#B9262B] bg-[#B9262B]" : "border-[#6B8DB3]"
                  }`}
                />
                <span className={`text-sm ${selected ? "text-white font-semibold" : "text-[#E6E6E8]"}`}>
                  {opt}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

const TextQuestionCard = ({ question, index, value, onChange }) => {
  return (
    <div
      className="p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]/70 transition-all hover:border-[#6B8DB3]/40"
      data-testid={`text-card-${question.id}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="font-mono text-xs text-[#B9262B] mt-1">
          {String(index).padStart(2, "0")}
        </div>
        <div className="flex-1">
          <div className="text-white font-medium text-base sm:text-lg leading-snug">
            {question.text}
          </div>
        </div>
      </div>
      <Textarea
        data-testid={`text-input-${question.id}`}
        rows={3}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Digite sua resposta..."
        className="bg-[#0e1015] border-[#3A3F47] text-white resize-none"
      />
    </div>
  );
};

const ReviewStep = ({ data, sections, questions, answers }) => {
  const scaleQuestions = questions.filter((q) => (q.question_type || "scale") === "scale");
  const financeQuestions = questions.filter((q) => q.question_type === "currency" || q.question_type === "number");
  const scaleAnswered = scaleQuestions.filter((q) => answers[q.id]?.score != null).length;
  const financeAnswered = financeQuestions.filter((q) => answers[q.id]?.value != null).length;

  return (
    <div className="fade-slide">
      <SectionHeader
        eyebrow="Quase lá"
        title="Revisar e finalizar"
        subtitle="Confira seus dados antes de gerar o diagnóstico."
      />

      <div className="mt-8 p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]/70">
        <div className="text-xs uppercase tracking-wider text-[#6B8DB3] mb-3">
          Dados da operação
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          {[
            ["Empresa", data.company],
            ["CNPJ", data.cnpj],
            ["Responsável", data.fleet_manager],
            ["Telefone", data.phone],
            ["E-mail", data.email],
            ["Consultor(a)", data.consultant],
            ["Data da auditoria", data.audit_date],
            ["Veículos", data.vehicles_qty],
            ["Motoristas", data.drivers_qty],
            ["Tipo de frota", data.fleet_type],
            ["Tipo de operação", data.operation_type],
            ["Cidade/UF", data.city_uf],
          ].map(([k, v]) => (
            <div
              key={k}
              className="flex justify-between gap-3 border-b border-[#3A3F47]/50 py-1.5"
            >
              <span className="text-[#6B8DB3]">{k}</span>
              <span className="text-white text-right">{v || "—"}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 p-6 rounded-xl border border-[#22B673]/40 bg-[#22B673]/5">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="text-[#22B673]" />
          <div>
            <div className="text-white font-semibold">
              {scaleAnswered} de {scaleQuestions.length} perguntas avaliativas respondidas
              {financeQuestions.length > 0 && (
                <span className="text-[#9aa3ad] text-sm font-normal">
                  {" "}· {financeAnswered}/{financeQuestions.length} indicadores financeiros
                </span>
              )}
            </div>
            <div className="text-xs text-[#9aa3ad]">
              Ao finalizar, geraremos seu score, diagnóstico e plano de ação.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreDiagnosticResultStep = ({ preview, loading, onContinue, onTerminate, submitting, hasMoreSections }) => {
  if (loading || !preview) {
    return (
      <div className="fade-slide text-center py-20">
        <Loader2 className="animate-spin mx-auto text-[#6B8DB3]" size={32} />
        <div className="mt-4 text-[#9aa3ad] text-sm">Calculando seu pré-diagnóstico...</div>
      </div>
    );
  }
  const score = Number(preview.total_score || 0).toFixed(1);
  const level = preview.level || preview.level_short || "—";
  const levelN = preview.level_number;
  return (
    <div className="fade-slide" data-testid="prediag-result-step">
      <SectionHeader
        eyebrow="Pré-diagnóstico"
        title="Resultado do seu pré-diagnóstico"
        subtitle="Com base nas suas respostas iniciais, identificamos uma visão preliminar sobre sua situação."
      />

      <div className="mt-10 grid sm:grid-cols-2 gap-4">
        <div className="p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]/70" data-testid="prediag-score-card">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#6B8DB3]">Score preliminar</div>
            <Gauge size={16} className="text-[#6B8DB3]" />
          </div>
          <div className="mt-3 font-bold text-5xl text-white">{score}<span className="text-xl text-[#6B8DB3]">%</span></div>
          <div className="mt-1 text-xs text-[#9aa3ad]">
            {preview.answered_questions} de {preview.total_prediag_questions} perguntas respondidas
          </div>
        </div>

        <div className="p-6 rounded-xl border border-[#B9262B]/40 bg-[#B9262B]/5" data-testid="prediag-level-card">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#B9262B]">Nível de maturidade</div>
            <TrendingUp size={16} className="text-[#B9262B]" />
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            {levelN && (
              <div className="font-bold text-5xl text-[#B9262B]">{levelN}</div>
            )}
            <div className="text-white font-semibold text-lg">{level}</div>
          </div>
          <div className="mt-1 text-xs text-[#9aa3ad]">Escala 1 (Inicial) a 5 (Avançado)</div>
        </div>
      </div>

      {preview.by_section?.length > 0 && (
        <div className="mt-6 p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]/70">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#6B8DB3] mb-4">
            Desempenho por seção
          </div>
          <div className="space-y-3">
            {preview.by_section.map((s) => (
              <div key={s.section_id} className="flex items-center justify-between gap-4">
                <div className="text-sm text-white font-medium flex-1 truncate">{s.name}</div>
                <div className="font-mono text-sm text-[#B9262B]">{Number(s.score || 0).toFixed(1)}%</div>
                <div className="text-[10px] uppercase tracking-wider text-[#22B673] w-24 text-right">
                  {s.level_short || "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 p-4 rounded-lg border border-[#6B8DB3]/30 bg-[#0A2342]/30">
        <div className="text-sm text-[#E6E6E8] leading-relaxed">
          <strong className="text-white">Este resultado é uma visão inicial</strong> baseada apenas nas respostas do pré-diagnóstico.
          {hasMoreSections
            ? " Para receber uma análise completa, continue preenchendo as próximas seções do formulário."
            : " Para finalizar e gerar o diagnóstico completo, prossiga para o resumo."}
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        <Button
          onClick={onTerminate}
          disabled={submitting}
          data-testid="prediag-terminate-btn"
          variant="outline"
          className="border-[#3A3F47] text-white hover:bg-[#1C1F26] h-12"
        >
          {submitting ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <>
              <XCircle size={16} className="mr-2" /> Encerrar agora
            </>
          )}
        </Button>
        <Button
          onClick={onContinue}
          disabled={submitting}
          data-testid="prediag-continue-btn"
          className="bg-[#B9262B] hover:bg-[#9a1e22] text-white font-semibold h-12 glow-accent"
        >
          {hasMoreSections ? "Continuar preenchimento" : "Continuar para o resumo"} <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

const TerminatedStep = ({ onRestart }) => (
  <div className="fade-slide text-center py-20" data-testid="prediag-terminated-step">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#22B673]/15 border border-[#22B673]/40 mb-6">
      <CheckCircle2 size={32} className="text-[#22B673]" />
    </div>
    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
      Pré-diagnóstico salvo com sucesso
    </h1>
    <p className="mt-4 text-[#9aa3ad] max-w-xl mx-auto leading-relaxed">
      Sua jornada foi encerrada com base no pré-diagnóstico. Nossos consultores receberam suas
      respostas iniciais e podem entrar em contato para apresentar oportunidades específicas para
      sua operação. Você pode realizar um novo preenchimento a qualquer momento.
    </p>
    <Button
      onClick={onRestart}
      data-testid="prediag-restart-btn"
      className="mt-8 bg-[#B9262B] hover:bg-[#9a1e22] text-white font-semibold px-8 h-12"
    >
      Voltar ao início
    </Button>
  </div>
);

export default Diagnostic;
